/**
 * Plans and pricing — the real ones, from the server.
 *
 * This page used to be marketing copy with invented numbers: ₹999, ten
 * companies, a fourteen-day trial, and a "redirecting to payment…" toast that
 * redirected nowhere. Every one of those was wrong, and a pricing page that
 * lies is worse than no pricing page.
 *
 * Now: the tiers come from /billing/plans, the account's own standing from
 * /billing/me, and Choose opens Razorpay. Nothing here decides anything —
 * prices are the server's, and access is granted by the payment webhook, never
 * by this screen.
 */

import { useState, useEffect, useCallback } from 'react';
import { TbStarFilled } from 'react-icons/tb';
import { FiCheck, FiLoader } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import { useUI } from '../context/UIContext';
import { billingAPI } from '../services/api';

const TEAL = '#145f5f';
const GOLD = '#8a6a1f';
const MUTED = '#7a7060';
const BORDER = '#ddd6c6';

const errText = (e) => e?.response?.data?.message || e?.message || 'Something went wrong.';

/** Razorpay's checkout script, loaded once, on demand. */
const loadCheckout = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
  return undefined;
});

/** What each plan gives you, in the user's terms rather than ours. */
const featuresOf = (plan) => [
  `Up to ${plan.maxCompanies} active companies`,
  `${plan.users} users on the account`,
  'AI assistant (Vee) — unlimited questions',
  'Document and resolution drafting',
  'Statutory registers and the UPSI register',
  'Deadline tracking and penalty estimates',
];

export default function PackagesPage() {
  const { showToast } = useUI();

  const [cycle, setCycle] = useState('annual');
  const [catalogue, setCatalogue] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, meRes] = await Promise.all([
        billingAPI.plans(),
        billingAPI.me().catch(() => null),
      ]);
      setCatalogue(plansRes?.data || null);
      setBilling(meRes?.data || null);
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const choose = async (plan) => {
    if (!catalogue?.paymentsEnabled) {
      showToast('Payments are not switched on for this server yet.', 'info');
      return;
    }
    setBusy(plan.key);
    try {
      const res = await billingAPI.checkout({ planKey: plan.key, cycle });
      const { checkout, quote, betterOffUpgrading } = res?.data || {};

      if (betterOffUpgrading) {
        showToast(`The ${betterOffUpgrading.name} plan would cost ${betterOffUpgrading.display} less.`, 'info');
      }

      const ready = await loadCheckout();
      if (!ready) {
        showToast('Could not reach the payment page. Check your connection and try again.', 'error');
        return;
      }

      const rzp = new window.Razorpay({
        ...checkout,
        handler: async (response) => {
          try {
            const verified = await billingAPI.verify(response);
            // The webhook is what actually activates it, so the message here
            // is deliberately about the payment, not about access.
            showToast(verified?.message || 'Payment received.', 'success');
            setTimeout(load, 2500); // give the webhook a moment, then re-read
          } catch (error) {
            showToast(errText(error), 'error');
          }
        },
        modal: {
          ondismiss: () => showToast('Payment cancelled — nothing was charged.', 'info'),
        },
      });
      rzp.on('payment.failed', (e) => showToast(e?.error?.description || 'The payment failed.', 'error'));
      rzp.open();

      showToast(`Opening payment for ${quote.display}.`, 'info');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setBusy('');
    }
  };

  const currentKey = billing?.plan?.key || null;
  const trialDays = catalogue?.trialDays ?? 7;

  return (
    <AppLayout pageTitle="Plans & Pricing">
      <PageHeader
        title="Plans and pricing"
        subtitle={`All prices exclude GST · ${trialDays}-day free trial on every new account`}
      />

      {/* Where this account stands, if it stands anywhere yet. */}
      {billing && (
        <Card className="mb-5">
          <div className="text-[13px]" style={{ color: '#08111f' }}>
            {billing.status === 'trialing' && billing.canCreate && (
              <>You are on the free trial — <strong>{billing.daysLeftInTrial} day{billing.daysLeftInTrial === 1 ? '' : 's'} left</strong>.</>
            )}
            {billing.plan && (
              <>You are on <strong>{billing.plan.name}</strong> ({billing.cycle}) — {billing.companies.used} of {billing.companies.allowance} active companies used.</>
            )}
            {!billing.canCreate && (
              <span style={{ color: '#9a564a' }}>{billing.reason}</span>
            )}
          </div>
        </Card>
      )}

      {/* Monthly / annual */}
      <div className="flex items-center gap-2 mb-5">
        {['annual', 'monthly'].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCycle(c)}
            className="px-4 py-1.5 rounded-full text-[12.5px] font-semibold capitalize transition"
            style={cycle === c
              ? { background: TEAL, color: '#fff' }
              : { background: '#fff', color: MUTED, border: `1px solid ${BORDER}` }}
          >
            {c}
          </button>
        ))}
        {cycle === 'annual' && (
          <span className="text-[11.5px]" style={{ color: GOLD }}>Billed once a year</span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[13px]" style={{ color: MUTED }}>
          <FiLoader className="animate-spin" /> Loading plans…
        </div>
      )}

      {!loading && catalogue && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            {catalogue.plans.map((plan) => {
              const price = plan[cycle];
              const isCurrent = plan.key === currentKey;
              return (
                <div
                  key={plan.key}
                  className="bg-white rounded-2xl border overflow-hidden flex flex-col"
                  style={{ borderColor: isCurrent ? TEAL : BORDER }}
                >
                  {isCurrent && (
                    <div className="px-4 py-1 text-[11px] font-bold text-white flex items-center gap-1" style={{ background: TEAL }}>
                      <TbStarFilled size={11} /> YOUR PLAN
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-[15px] font-bold" style={{ color: '#08111f' }}>{plan.name}</div>
                    <div className="mt-2 mb-1">
                      <span className="text-[26px] font-bold" style={{ color: '#08111f' }}>{price.display}</span>
                      <span className="text-[12px]" style={{ color: MUTED }}>/{cycle === 'annual' ? 'year' : 'month'}</span>
                    </div>
                    <div className="text-[11.5px] mb-4" style={{ color: MUTED }}>
                      + GST · {`₹${Math.round(price.total / 100).toLocaleString('en-IN')}`} total
                    </div>

                    <ul className="space-y-1.5 mb-5 flex-1">
                      {featuresOf(plan).map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-[12px]" style={{ color: '#3a3a3a' }}>
                          <FiCheck size={13} className="shrink-0 mt-0.5" style={{ color: TEAL }} />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <Button
                      variant={isCurrent ? 'outline' : 'primary'}
                      disabled={isCurrent || busy === plan.key}
                      loading={busy === plan.key}
                      onClick={() => choose(plan)}
                      className="w-full"
                    >
                      {isCurrent ? 'Current plan' : 'Choose'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <Card className="mb-5">
            <div className="text-[13px] font-semibold mb-1.5" style={{ color: '#08111f' }}>Need more companies?</div>
            <div className="text-[12.5px] leading-relaxed" style={{ color: MUTED }}>
              Extra companies can be added to any plan: {catalogue.addOns.single.display} for one,
              or {catalogue.addOns.pack5.display} for five. Past {catalogue.customAbove} companies we
              write a plan for you — talk to us.
            </div>
            <div className="text-[12px] mt-2" style={{ color: GOLD }}>
              A company only counts once you do something with it. Adding one and leaving it alone is free.
            </div>
          </Card>

          {!catalogue.paymentsEnabled && (
            <Card>
              <div className="text-[12.5px]" style={{ color: GOLD }}>
                Payments are not switched on for this server yet, so Choose will not open a payment page.
                Everything else on this page is live.
              </div>
            </Card>
          )}
        </>
      )}
    </AppLayout>
  );
}
